import React from 'react';
import Select from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import { string, func, shape, bool, node } from 'prop-types';

import countryData from 'resources/data/countries';
import { find } from 'lodash';

const suggestions = countryData.map(suggestion => ({
  value: suggestion.code,
  label: suggestion.name
}));

const styles = theme => ({
  root: {
    flexGrow: 1,
    height: 64
  },
  input: {
    display: 'flex',
    padding: 0
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden'
  },
  chip: {
    margin: `${theme.spacing(1 / 2)}px ${theme.spacing(1 / 4)}px`
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light'
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    )
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
  },
  singleValue: {
    fontSize: 16
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0
  },
  divider: {
    height: theme.spacing(2)
  }
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}
NoOptionsMessage.propTypes = {
  selectProps: shape({
    classes: shape({
      noOptionsMessage: string
    })
  }),
  children: node,
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  })
};

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}
inputComponent.propTypes = {
  inputRef: func
};

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps
        }
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}
Control.propTypes = {
  selectProps: shape({
    classes: shape({
      input: string
    }),
    textFieldProps: shape({
      InputLabelProps: shape({
        shrink: bool
      }),
      error: bool,
      helperText: string,
      label: string
    })
  }),
  innerRef: func,
  children: node,
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  })
};

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}
Option.propTypes = {
  innerRef: func,
  isFocused: bool,
  isSelected: bool,
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  }),
  children: node
};

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}
Placeholder.propTypes = {
  selectProps: shape({
    classes: shape({
      placeholder: string
    })
  }),
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  }),
  children: node
};

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}
SingleValue.propTypes = {
  selectProps: shape({
    classes: shape({
      singleValue: string
    })
  }),
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  }),
  children: node
};

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}
ValueContainer.propTypes = {
  selectProps: shape({
    classes: shape({
      valueContainer: string
    })
  }),
  children: node
};

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}
Menu.propTypes = {
  children: node,
  selectProps: shape({
    classes: shape({
      paper: string
    })
  }),
  innerProps: shape({
    onMouseDown: func,
    onTouchEnd: func
  })
};

const components = {
  Control,
  Menu,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};

const getDefaultSearchText = value => {
  const currentCountry = find(countryData, ['code', value]);
  if (!currentCountry) {
    return value;
  }
  return currentCountry.name;
};

class CountrySelectField extends React.Component {
  static propTypes = {
    onChange: func,
    input: shape({
      value: string
    }),
    className: string,
    label: string,
    meta: shape({
      touched: bool,
      error: string,
      warning: string
    })
  };
  state = {
    searchText: {
      value: this.props.input.value,
      label: getDefaultSearchText(this.props.input.value)
    }
  };

  handleChange = value => {
    this.setState({
      searchText: value
    });
    const { input } = this.props;
    input.onChange(value ? value.value : null);
  };

  render() {
    const {
      classes,
      theme,
      input,
      label,
      meta: { touched, error, warning }
    } = this.props;
    const { searchText } = this.state;
    const _error = touched && (error || warning);

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit'
        }
      })
    };

    return (
      <div className={classes.root}>
        <NoSsr>
          <Select
            classes={classes}
            styles={selectStyles}
            options={suggestions}
            components={components}
            value={searchText}
            onChange={this.handleChange}
            textFieldProps={{
              label: label,
              error: _error,
              helperText: _error ? _error : '',
              InputLabelProps: {
                shrink: true
              }
            }}
            placeholder="Search a country (start with a)"
            isClearable
            name={input.name}
          />
          <div className={classes.divider} />
        </NoSsr>
      </div>
    );
  }
}

CountrySelectField.propTypes = {
  classes: shape({
    chip: string,
    chipFocused: string,
    divider: string,
    input: string,
    noOptionsMessage: string,
    paper: string,
    placeholder: string,
    root: string,
    singleValue: string,
    valueContainer: string
  }),
  theme: shape({
    pallette: shape({
      text: shape({
        primary: string
      })
    })
  })
};

export default withStyles(styles, { withTheme: true })(CountrySelectField);



// WEBPACK FOOTER //
// ./src/client/shared-components/CountrySelectField/index.js